import {
  Box,
  Flex,
  Heading,
  Text,
  Skeleton,
  useToast,
  Link,
  HStack,
  Spacer,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  SimpleGrid,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import {
  useParams,
  useNavigate,
  Link as ReactRouterLink,
} from 'react-router-dom';
import { z } from 'zod';

import {
  date as commonDate,
  utils as commonUtils,
  finleyAssert,
} from '@finley-technologies/common';

import {
  GetDeliverableContainerReceiptsDocumentGQLType,
  ReceiptZipFileDownloadAvailableGQLType,
  useAreDeliverablesReceiptsZipFilesAvailableForDownloadQuery,
  useCreateDeliverableContainerReceiptActionMutation,
  useCreateNewThreadWithCommentForDeliverableContainerMutation,
  useGetDeliverableContainerReceiptsQuery,
  useGetDeliverableContainerThreadsQuery,
  useGetUserQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { DigitalMonthlyReport } from '../digitalMonthlyReport/DigitalMonthlyReportContainer';
import { DownloadButton, DownloadMenuZipFiles } from '../DownloadButton';
import { ReceiptStatusCell } from '../Status';
import { ContainerUpdatesDrawer } from '../UpdatesDrawer';
import {
  getDeliverableDetailViewUrl,
  getDeliverableFinalOutputReceiptsViewUrl,
} from '../utils';
import { DeliverableReceiptsInputTable } from './DeliverableReceiptsInputTable';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { RichTextEditor } from 'packages/common-frontend/components/RichTextEditor/RichTextEditor';
import { errorToastConfig } from 'packages/common-frontend/constants';

// Types
const RouteParamsStruct = z.object({
  deliverableId: z.string({ message: 'Missing URL parameter' }),
  receiptId: z.string({ message: 'Missing URL parameter' }),
});

type MarkReadyForReviewModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (comment: string) => void;
};
const MarkReadyForReviewModal = ({
  isOpen,
  onCancel,
  onSubmit,
}: MarkReadyForReviewModalProps) => {
  const cancelRef = React.useRef(null);
  const [comment, setComment] = useState<string>('');

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onCancel}
      size="xl"
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Mark Ready For Review
          </AlertDialogHeader>

          <AlertDialogBody>
            <Text mb={6}>
              Are you sure you want to share this report with the capital
              provider?
            </Text>
            <Box mb={4}>
              <RichTextEditor
                text={comment}
                setText={setComment}
                isEditable
                placeHolderText="Optional"
              />
            </Box>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button
              ref={cancelRef.current}
              onClick={onCancel}
              _focus={{ boxShadow: 'none' }}
              borderColor={colors.aurora[400]}
              borderWidth="1px"
              bgColor="white"
              color={colors.aurora[400]}
            >
              Cancel
            </Button>
            <Button
              colorScheme="aurora"
              onClick={() => {
                onSubmit(comment);
              }}
              ml={3}
              _focus={{ boxShadow: 'none' }}
            >
              Submit for Review
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export const DeliverableContainerReceipts = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { deliverableId, receiptId } = RouteParamsStruct.parse(params);
  const toast = useToast();

  finleyAssert(receiptId, 'receiptId is required');
  finleyAssert(deliverableId, 'deliverableId is required');

  const {
    data: areZipFilesAvailableData,
    startPolling: startAreZipFilesAvailablePolling,
    stopPolling: stopAreZipFilesAvailablePolling,
  } = useAreDeliverablesReceiptsZipFilesAvailableForDownloadQuery({
    fetchPolicy: 'network-only',
    variables: {
      receiptId,
    },
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  const [
    canDownloadZipFiles,
    setCanDownloadZipFiles,
  ] = useState<ReceiptZipFileDownloadAvailableGQLType>();

  // can download zip files is not updating when we switch between receipts, so forcing that here
  useEffect(() => {
    setCanDownloadZipFiles(undefined);
  }, [receiptId]);

  useEffect(() => {
    if (areZipFilesAvailableData) {
      const canDownload =
        areZipFilesAvailableData.areDeliverablesReceiptsZipFilesAvailableForDownload;
      // if input is downloadable, show zip file download
      if (canDownload.canDownloadInput) {
        stopAreZipFilesAvailablePolling();
        setCanDownloadZipFiles(canDownload);
      } else {
        startAreZipFilesAvailablePolling(5000);
      }
    }
  }, [
    areZipFilesAvailableData,
    startAreZipFilesAvailablePolling,
    stopAreZipFilesAvailablePolling,
  ]);

  const {
    data: receiptsData,
    error: receiptsError,
    refetch: refetchReceipts,
  } = useGetDeliverableContainerReceiptsQuery({
    fetchPolicy: 'network-only',
    variables: {
      containerId: deliverableId,
    },
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  const [
    /**
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    createDeliverableContainerReceiptActionMutation,
  ] = useCreateDeliverableContainerReceiptActionMutation({
    fetchPolicy: 'network-only',
    refetchQueries: [
      {
        query: GetDeliverableContainerReceiptsDocumentGQLType,
        variables: { containerId: deliverableId },
      },
    ],
    onError(error) {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : error.message;
      toast(errorToastConfig);
    },
  });
  const [
    isMarkForReviewModalOpen,
    setIsMarkForReviewModalOpen,
  ] = useState<boolean>(false);

  const {
    data: threadsData,
    refetch: refetchThreadsData,
  } = useGetDeliverableContainerThreadsQuery({
    fetchPolicy: 'network-only',
    variables: {
      containerId: deliverableId,
    },
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });
  const [
    createNewThreadWithCommentForDeliverableContainerMutation,
  ] = useCreateNewThreadWithCommentForDeliverableContainerMutation({
    fetchPolicy: 'network-only',
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });
  const [commentsDrawerOpen, setCommentsDrawerOpen] = useState<boolean>(false);

  const { data: userData } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  if (!receiptsData || !userData || !userData.user || !threadsData) {
    return (
      <Skeleton
        height="600px"
        data-testid="deliverables-final-output-report-versions-view-loading"
      />
    );
  }

  if (receiptsData.deliverableContainerReceipts.length === 0) {
    toast({ title: 'No existing versions.' });
    navigate(`${getDeliverableDetailViewUrl(deliverableId)}`);
  }

  if (receiptsError) {
    toast({ title: `Failed to load page.` });
    navigate('/deliverables');
  }

  const receipts = receiptsData.deliverableContainerReceipts;
  const currentReceipt = receipts.find((r) => r.id === receiptId);

  if (!currentReceipt) {
    toast({ title: 'Could not find version.' });
    navigate(`${getDeliverableDetailViewUrl(deliverableId)}`);
    return (
      <Skeleton
        height="600px"
        data-testid="deliverables-final-output-report-versions-view-error"
      />
    );
  }

  const {
    id: userId,
    organizationId,
    organization: { slug: orgSlug },
  } = userData.user;

  const detailViewTitle = `${commonUtils.toTitleCase(
    orgSlug,
  )} - ${commonUtils.toTitleCase(currentReceipt.containerName)}`;

  const lastFinalArtifact =
    currentReceipt.finalArtifactsInstances.length > 0
      ? currentReceipt.finalArtifactsInstances[
          currentReceipt.finalArtifactsInstances.length - 1
        ]
      : null;
  const filename = `${currentReceipt.creditFacilitySlug}-${currentReceipt.containerType}-${currentReceipt.containerDueDate}-v${currentReceipt.version}`;
  const lastFinalArtifactFilename = lastFinalArtifact
    ? `${filename}-${lastFinalArtifact.artifactConfigKey}-${lastFinalArtifact.artifactReportingDate}`
    : null;

  return (
    <Box height="100%">
      <Flex justify="space-between" flexDirection="column">
        <Text fontSize="xs" color="gray.800" letterSpacing="wide" pb="16px">
          <Link as={ReactRouterLink} to="/deliverables" fontWeight="semibold">
            Deliverables
          </Link>
          {' > '}
          <Link
            as={ReactRouterLink}
            to={`/deliverables/${deliverableId}`}
            fontWeight="semibold"
          >
            {detailViewTitle}
          </Link>{' '}
          {`> Report Version ${currentReceipt.version}`}
        </Text>
        <HStack spacing="20px">
          <Heading as="h2" fontSize="3xl">
            {`${commonUtils.toTitleCase(
              currentReceipt.containerName,
            )} Version ${
              currentReceipt.version
            } - ${commonDate.fullDateStrToShortMonthDayString(
              currentReceipt.containerDueDate,
            )}`}
          </Heading>
          {currentReceipt.expired ? (
            <Tooltip
              label="A more recent version has been submitted to the Capital Provider."
              aria-label="expired-tooltip"
              placement="right"
            >
              <HStack
                alignItems="center"
                px="14px"
                py="10px"
                borderRadius="8px"
                bgColor="gray.100"
                width="fit-content"
              >
                <Text color="gray.500" fontSize="sm" fontWeight="semibold">
                  Expired
                </Text>
              </HStack>
            </Tooltip>
          ) : (
            <ReceiptStatusCell
              mostRecentAction={currentReceipt.actionsLog[0].action}
            />
          )}

          <Spacer />
          <Tooltip label="Updates" placement="left">
            <IconButton
              aria-label="Updates"
              key="Updates"
              icon={<Icon icon="message-lines" />}
              _focus={{ boxShadow: 'none' }}
              onClick={() => {
                setCommentsDrawerOpen(true);
              }}
            />
          </Tooltip>
          {canDownloadZipFiles && (
            <DownloadMenuZipFiles
              py={5}
              px={5}
              justifyContent="flex-start"
              variant="outline"
              size="sm"
              borderColor={colors.aurora[400]}
              borderWidth="2px"
              color={colors.aurora[400]}
              _focus={{ boxShadow: 'none' }}
              rightIcon={<Icon icon="chevron-down" />}
              fileName={filename}
              receiptId={receiptId}
              canDownloadZipFiles={canDownloadZipFiles}
            />
          )}
          {!canDownloadZipFiles &&
            lastFinalArtifact &&
            lastFinalArtifactFilename && (
              <DownloadButton
                py={5}
                px={5}
                justifyContent="flex-start"
                variant="outline"
                size="sm"
                borderColor={colors.aurora[400]}
                borderWidth="2px"
                color={colors.aurora[400]}
                _focus={{ boxShadow: 'none' }}
                text="Download"
                fileName={lastFinalArtifactFilename}
                artifactInstanceId={lastFinalArtifact.receiptInstanceId}
              />
            )}

          <Button
            py={5}
            px={6}
            justifyContent="flex-start"
            variant="solid"
            _focus={{ boxShadow: 'none' }}
            size="sm"
            colorScheme="aurora"
            color="white"
            isDisabled={
              currentReceipt.expired ||
              currentReceipt.actionsLog[0].action !== 'generated'
            }
            onClick={() => {
              setIsMarkForReviewModalOpen(true);
            }}
          >
            {currentReceipt.actionsLog[0].action === 'generated'
              ? 'Submit to Capital Provider'
              : 'Submitted to Capital Provider'}
          </Button>
        </HStack>

        <HStack mt={2} mb={2}>
          {!currentReceipt.expired &&
          currentReceipt.actionsLog[0].action !== 'generated' ? (
            <>
              <Spacer />
              <Box maxWidth="240px">
                <Text
                  fontSize="xs"
                  color="gray.500"
                >{`Version ${currentReceipt.version} has been shared with your capital provider.`}</Text>
              </Box>
            </>
          ) : (
            <Box mb={8} />
          )}
        </HStack>

        <SimpleGrid columns={3} spacingX={10} spacingY="4px" mb={8}>
          <Text fontSize="xs" color="gray.600">
            Credit Facility
          </Text>
          <Text fontSize="xs" color="gray.600">
            Deliverable Type
          </Text>
          <Text fontSize="xs" color="gray.600">
            Due Date
          </Text>

          <Text as="b" fontSize="sm">
            {commonUtils.toTitleCase(currentReceipt.creditFacilitySlug)}
          </Text>
          <Text as="b" fontSize="sm">
            {commonUtils.toTitleCase(currentReceipt.containerType)}
          </Text>
          <Text as="b" fontSize="sm">
            {commonDate.fullDateStrToHumanDateString(
              currentReceipt.containerDueDate,
            )}
          </Text>
        </SimpleGrid>
        <HStack mt={8} spacing="20px" mb={8}>
          <Text fontWeight="bold" fontSize="sm">
            Version
          </Text>
          <Menu>
            <MenuButton
              as={Button}
              _focus={{ boxShadow: 'none' }}
              size="sm"
              padding="20px"
              width="300px"
              textAlign="start"
              bgColor="white"
              borderWidth="1px"
              fontWeight="normal"
              rightIcon={<Icon icon="chevron-down" />}
            >
              {currentReceipt.version}
            </MenuButton>
            <MenuList width="300px">
              {receipts.map((r) => {
                return (
                  <MenuItem
                    key={r.id}
                    onClick={() => {
                      // don't reload page if they click on the current receipt they are viewing
                      if (r.id !== currentReceipt.id) {
                        navigate(
                          getDeliverableFinalOutputReceiptsViewUrl({
                            containerId: deliverableId,
                            receiptId: r.id,
                          }),
                        );
                      }
                    }}
                  >
                    {r.version}
                  </MenuItem>
                );
              })}
            </MenuList>
          </Menu>
        </HStack>

        {currentReceipt.containerType === 'settlementReport' &&
        currentReceipt.finalArtifactsInstances.length > 0 ? (
          <DigitalMonthlyReport currentReceipt={currentReceipt} />
        ) : (
          <>
            <Heading as="h2" fontSize="24px" mb="24px">
              Inputs
            </Heading>
            <DeliverableReceiptsInputTable currentReceipt={currentReceipt} />
          </>
        )}

        <ContainerUpdatesDrawer
          isOpen={commentsDrawerOpen}
          onClose={() => {
            setCommentsDrawerOpen(false);
          }}
          deliverableContainerThreads={threadsData.deliverableContainerThreads}
          receiptsActionLog={currentReceipt.actionsLog.map((action) => ({
            ...action,
            version: currentReceipt.version,
          }))}
          organizationId={organizationId}
          externalOrgOptions={currentReceipt.capitalProviderEntities.map(
            (obj) => ({ value: obj.id, label: _.startCase(obj.slug) }),
          )}
          reportName={`${commonUtils.toTitleCase(
            currentReceipt.containerType,
          )} - ${commonDate.fullDateStrToShortMonthDayString(
            currentReceipt.containerDueDate,
          )}`}
          creditFacilitySlug={currentReceipt.creditFacilitySlug}
          userId={userId}
          containerId={deliverableId}
          refetchThreads={async () => {
            await refetchThreadsData();
          }}
        />

        <MarkReadyForReviewModal
          isOpen={isMarkForReviewModalOpen}
          onCancel={() => {
            setIsMarkForReviewModalOpen(false);
          }}
          onSubmit={async (comment: string) => {
            // mutation
            await createDeliverableContainerReceiptActionMutation({
              variables: {
                userId,
                organizationId,
                receiptId: currentReceipt.id,
                action: 'submitted',
              },
            });

            // refetch receipts
            refetchReceipts();

            if (comment.length > 0) {
              await createNewThreadWithCommentForDeliverableContainerMutation({
                variables: {
                  organizationIds: [
                    currentReceipt.borrowerId,
                    ...currentReceipt.capitalProviderEntities.map(
                      (obj) => obj.id,
                    ),
                  ],
                  comment,
                  authorUserId: userId,
                  deliverableContainerId: deliverableId,
                },
              });
              refetchThreadsData();
            }

            // close modal
            setIsMarkForReviewModalOpen(false);
          }}
        />
      </Flex>
    </Box>
  );
};
